import { addDays, format, isAfter, isBefore, parseISO, startOfDay } from "date-fns"

import type {
  DayOfWeek,
  PlannedRoute,
  PlannedStop,
  RouteInstance,
  RoutePlanned,
  SlotType,
  StopInstance,
} from "@/types/collections"

/**
 * Converting between the planning timeline's in-memory routes and the shapes the database hands
 * back, and comparing an edited route with the one it started as.
 *
 * RoutePlanned is the DB/API shape (snake_case, effective_from string). PlannedRoute is the UI
 * shape (camelCase, date: Date). A route instance, when one has been generated for a date, wins
 * over the planned template for that date: its stops and times are what the day will run.
 */

const DAY_MS = 1000 * 60 * 60 * 24

/** The slot types as the new-stop form names them. */
export type UISlotType =
  | "Shipper Collection"
  | "Internal Stop"
  | "Haulage Collection"
  | "Haulage Delivery"
  | "Depot Injection"
  | "Direct Injection"
  | "Charging Stop"
  | "Stand-by"

export const uiSlotTypeToDbSlotType = (uiType: UISlotType): SlotType => {
  const map: Record<UISlotType, SlotType> = {
    "Shipper Collection": "shipper_collection",
    "Internal Stop": "internal_stop",
    "Haulage Collection": "haulage_collection",
    "Haulage Delivery": "haulage_delivery",
    "Depot Injection": "depot_injection",
    "Direct Injection": "direct_injection",
    "Charging Stop": "charging_stop",
    "Stand-by": "stand_by",
  }
  return map[uiType]
}

export const dbSlotTypeToUiSlotType = (dbType: SlotType): UISlotType => {
  const map: Record<SlotType, UISlotType> = {
    shipper_collection: "Shipper Collection",
    internal_stop: "Internal Stop",
    haulage_collection: "Haulage Collection",
    haulage_delivery: "Haulage Delivery",
    depot_injection: "Depot Injection",
    direct_injection: "Direct Injection",
    charging_stop: "Charging Stop",
    stand_by: "Stand-by",
  }
  return map[dbType]
}

/** A date-only string as yyyy-MM-dd, read the way every instance lookup here reads it. */
export const toDateOnly = (d: string | Date) => format(new Date(d), "yyyy-MM-dd")

/** The instance generated for a planned route on a date, if any. */
export function findRouteInstanceOn(
  instances: RouteInstance[] | undefined,
  plannedRouteId: string,
  date: Date,
): RouteInstance | undefined {
  const dateStr = format(date, "yyyy-MM-dd")
  return instances?.find(
    (ri) => ri.planned_route_id === plannedRouteId && toDateOnly(ri.date) === dateStr,
  )
}

export function dayOfWeekOf(date: Date): DayOfWeek {
  const dayMap: DayOfWeek[] = [
    "sunday",
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
  ]
  return dayMap[date.getDay()]
}

/** A stored HH:mm(:ss) time on the route's effective-from date, plus a day offset for overnight. */
export function parseTimeWithOffset(effectiveFrom: string, time: string, dayOffset = 0): Date {
  // parseISO handles date-only strings as local time (unlike new Date() which uses UTC)
  const baseDate = parseISO(effectiveFrom)
  if (dayOffset > 0) {
    baseDate.setDate(baseDate.getDate() + dayOffset)
  }
  const timeParts = time.split(":")
  baseDate.setHours(parseInt(timeParts[0], 10), parseInt(timeParts[1], 10), 0, 0)
  return baseDate
}

export function routePlannedToPlannedRoute(rp: RoutePlanned): PlannedRoute {
  return {
    id: rp.id,
    code: rp.code,
    date: parseISO(rp.effective_from),
    plannedStartTime: parseTimeWithOffset(rp.effective_from, rp.default_start_time),
    plannedEndTime: parseTimeWithOffset(rp.effective_from, rp.default_end_time),
    stops:
      rp.stops?.map((stop) => ({
        id: stop.id,
        routeId: rp.id,
        sequence: stop.sequence,
        type: "collection" as const,
        locationId: stop.location_id,
        slotId: stop.availability_slot_id,
        name: stop.location?.site_name || "",
        address: stop.location?.address || "",
        postcode: stop.location?.postcode || "",
        lat: stop.location?.latitude || 0,
        lng: stop.location?.longitude || 0,
        plannedArrival: parseTimeWithOffset(
          rp.effective_from,
          stop.planned_arrival,
          stop.arrival_day_offset || 0,
        ),
        plannedDeparture: parseTimeWithOffset(
          rp.effective_from,
          stop.planned_departure,
          stop.departure_day_offset || 0,
        ),
        serviceTimeMinutes: stop.service_time_minutes,
        // Planned stops carry no forecast; it lives on the stop instances.
        volumeEstimated: 0,
        loadingUnitsEstimated: 0,
        slotType: stop.stop_type,
        slotServices: stop.stop_services,
        slotPnl: stop.stop_pnl,
        haulageDeliveryRevenueGbp: stop.haulage_delivery_revenue_gbp ?? undefined,
        directInjectionCostGbp: stop.direct_injection_cost_gbp ?? undefined,
      })) || [],
    totalStops: rp.stops?.length || 0,
    depot: rp.depot,
    estimatedCost: rp.estimated_cost,
    isRecurring: rp.days && rp.days.length > 0,
    recurringDays: rp.days,
    recurringEndDate: rp.effective_to ? new Date(rp.effective_to) : undefined,
    excludedDates: (rp.excluded_dates ?? []).map((d: string) => new Date(d)),
    status: rp.status,
    createdAt: new Date(rp.created_at),
    updatedAt: new Date(rp.updated_at),
    category: rp.category,
    operator: rp.operator,
    operatorAllocations: rp.operator_allocations,
    vehicleType: rp.vehicle_type,
    crewSize: rp.crew_size,
    route_kind: rp.route_kind,
  }
}

export function stopInstanceToPlannedStop(s: StopInstance, routeId: string): PlannedStop {
  return {
    id: s.id,
    routeId,
    sequence: s.sequence ?? 0,
    // Planned stops are collections throughout the timeline; the finer distinction lives on
    // slotType.
    type: "collection" as const,
    locationId: s.location_id,
    name: s.name ?? "",
    address: s.address ?? "",
    postcode: s.postcode ?? "",
    lat: Number(s.lat ?? 0),
    lng: Number(s.lng ?? 0),
    plannedArrival: s.planned_arrival ? new Date(s.planned_arrival) : new Date(),
    plannedDeparture: s.planned_departure ? new Date(s.planned_departure) : new Date(),
    serviceTimeMinutes: s.service_time_minutes ?? 30,
    volumeEstimated: s.volume_forecast ?? 0,
    loadingUnitsEstimated: s.loading_units_forecast ?? 0,
    slotType: s.stop_type ?? "shipper_collection",
    slotServices: s.slot_services ?? [],
    slotPnl: s.slot_pnl ?? [],
    haulageDeliveryRevenueGbp: s.haulage_delivery_revenue_gbp ?? undefined,
    directInjectionCostGbp: s.direct_injection_cost_gbp ?? undefined,
  }
}

/** An instance's stops as planned stops, in sequence order. */
export function instanceStopsToPlannedStops(stops: StopInstance[], routeId: string): PlannedStop[] {
  return stops
    .slice()
    .sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0))
    .map((s) => stopInstanceToPlannedStop(s, routeId))
}

/** The route as the instance generated for `date` runs it: the instance's times and stops. */
export function applyInstanceToRoute<T extends PlannedRoute>(
  route: T,
  ri: RouteInstance,
  date: Date,
): T {
  const stops = instanceStopsToPlannedStops(ri.stops ?? [], route.id)
  return {
    ...route,
    date,
    plannedStartTime: ri.planned_start_time
      ? new Date(ri.planned_start_time)
      : route.plannedStartTime,
    plannedEndTime: ri.planned_end_time ? new Date(ri.planned_end_time) : route.plannedEndTime,
    stops,
    totalStops: stops.length,
  }
}

/** A copy whose Date fields are fresh objects, so editing one copy cannot move the other. */
export function cloneRouteDates<T extends PlannedRoute>(route: T): T {
  return {
    ...route,
    date: new Date(route.date),
    plannedStartTime: new Date(route.plannedStartTime),
    plannedEndTime: new Date(route.plannedEndTime),
    recurringEndDate: route.recurringEndDate ? new Date(route.recurringEndDate) : undefined,
    createdAt: new Date(route.createdAt),
    updatedAt: new Date(route.updatedAt),
    stops: route.stops.map((stop) => ({
      ...stop,
      plannedArrival: new Date(stop.plannedArrival),
      plannedDeparture: new Date(stop.plannedDeparture),
    })),
  }
}

/**
 * Stops re-anchored from one date to another, keeping each stop's clock time and its day offset
 * from `fromDate` -- so a stop the morning after the route started is still the morning after.
 */
export function shiftStopsToDate(
  stops: PlannedStop[],
  fromDate: Date,
  toDate: Date,
): PlannedStop[] {
  const fromDay = startOfDay(fromDate)
  const onTarget = (time: Date) => {
    const offset = Math.round((startOfDay(time).getTime() - fromDay.getTime()) / DAY_MS)
    return new Date(`${format(addDays(toDate, offset), "yyyy-MM-dd")}T${format(time, "HH:mm:ss")}`)
  }
  return stops.map((stop) => ({
    ...stop,
    plannedArrival: onTarget(stop.plannedArrival),
    plannedDeparture: onTarget(stop.plannedDeparture),
  }))
}

/**
 * A recurring route's template laid on `date`. An end at or before the start is the next
 * morning; without the roll an overnight route saved from this row was written ending before it
 * began.
 */
export function templateRouteForDate(route: PlannedRoute, date: Date): PlannedRoute {
  const dateStr = format(date, "yyyy-MM-dd")
  const templateStart = new Date(`${dateStr}T${format(route.plannedStartTime, "HH:mm:ss")}`)
  let templateEnd = new Date(`${dateStr}T${format(route.plannedEndTime, "HH:mm:ss")}`)
  if (templateEnd <= templateStart) templateEnd = addDays(templateEnd, 1)
  return {
    ...route,
    date,
    plannedStartTime: templateStart,
    plannedEndTime: templateEnd,
    stops: shiftStopsToDate(route.stops, route.date, date),
  }
}

/**
 * Does the search box match this route? Tested against the source route: an instance-derived
 * row can carry different stops, and matching on those would change which routes a search finds.
 * No driver or plate clause: these rows are planned routes, and an assignment is only ever made
 * against a date. Search one on the Monitoring tab.
 */
export function routeMatchesSearch(route: PlannedRoute, searchTerm: string): boolean {
  const term = searchTerm.toLowerCase()
  return (
    route.code.toLowerCase().includes(term) ||
    route.stops.some(
      (s) =>
        s.name.toLowerCase().includes(term) ||
        (s.address?.toLowerCase().includes(term) ?? false) ||
        (s.postcode?.toLowerCase().includes(term) ?? false),
    )
  )
}

/**
 * Every route that runs on `date`, as it runs that day. Where an instance exists for the date,
 * the route from `routes` (seeded with the instance so move/edit/remove work) is shown instead of
 * the planned template.
 */
export function expandRoutesForDate(
  routes: PlannedRoute[],
  date: Date,
  routeInstances: RouteInstance[] | undefined,
): PlannedRoute[] {
  const selectedDayStart = startOfDay(date)
  const selectedDayOfWeek = dayOfWeekOf(date)
  const isExcluded = (route: PlannedRoute) =>
    (route.excludedDates ?? []).some(
      (d: Date) => startOfDay(d).getTime() === selectedDayStart.getTime(),
    )
  const fromInstance = (route: PlannedRoute, ri: RouteInstance) => {
    const routeFromState = routes.find((r) => r.id === route.id)
    return routeFromState
      ? { ...routeFromState, date }
      : applyInstanceToRoute(route, ri, new Date(ri.date))
  }

  const result: PlannedRoute[] = []
  for (const route of routes) {
    const routeStartDate = startOfDay(route.date)

    if (!route.isRecurring || !route.recurringDays || route.recurringDays.length === 0) {
      if (routeStartDate.getTime() === selectedDayStart.getTime() && !isExcluded(route)) {
        const ri = findRouteInstanceOn(routeInstances, route.id, date)
        result.push(ri && ri.stops?.length ? fromInstance(route, ri) : route)
      }
      continue
    }

    // A recurring route runs from its effective-from date, up to and including its
    // effective-to date, on its recurring days, except on its excluded dates.
    if (isBefore(selectedDayStart, routeStartDate)) continue
    if (route.recurringEndDate && isAfter(selectedDayStart, startOfDay(route.recurringEndDate))) {
      continue
    }
    if (!route.recurringDays.includes(selectedDayOfWeek)) continue
    if (isExcluded(route)) continue

    const ri = findRouteInstanceOn(routeInstances, route.id, date)
    result.push(
      ri && ri.stops?.length ? fromInstance(route, ri) : templateRouteForDate(route, date),
    )
  }
  return result
}

/** What changed between route and original, for the save confirmation dialog. */
export function describeRouteChanges(
  route: PlannedRoute,
  original: PlannedRoute | undefined,
): string[] {
  if (!original) return ["New route"]
  const changes: string[] = []
  if (route.code !== original.code) changes.push("Route code")
  if (route.depot !== original.depot) changes.push("Depot")
  if (route.status !== original.status) changes.push("Status")
  if (route.isRecurring !== original.isRecurring) changes.push("Recurrence")
  const origDays = [...(original.recurringDays || [])].sort().join(",")
  const currDays = [...(route.recurringDays || [])].sort().join(",")
  if (origDays !== currDays) changes.push("Recurring days")
  if (format(route.plannedStartTime, "HH:mm") !== format(original.plannedStartTime, "HH:mm"))
    changes.push("Start time")
  if (format(route.plannedEndTime, "HH:mm") !== format(original.plannedEndTime, "HH:mm"))
    changes.push("End time")
  if (route.stops.length !== original.stops.length) {
    changes.push(
      route.stops.length > original.stops.length
        ? `${route.stops.length - original.stops.length} stop(s) added`
        : `${original.stops.length - route.stops.length} stop(s) removed`,
    )
  }
  const maxStops = Math.max(route.stops.length, original.stops.length)
  let stopChanges = 0
  for (let i = 0; i < maxStops; i++) {
    const s = route.stops[i]
    const o = original.stops[i]
    if (!s || !o) {
      stopChanges++
      continue
    }
    if (s.locationId !== o.locationId || s.sequence !== o.sequence) stopChanges++
    else if (
      format(s.plannedArrival, "HH:mm") !== format(o.plannedArrival, "HH:mm") ||
      format(s.plannedDeparture, "HH:mm") !== format(o.plannedDeparture, "HH:mm")
    )
      stopChanges++
  }
  if (stopChanges > 0)
    changes.push(stopChanges === 1 ? "1 stop updated" : `${stopChanges} stops updated`)
  // Stop-level revenue (e.g. haulage delivery)
  for (let i = 0; i < maxStops; i++) {
    const s = route.stops[i]
    const o = original.stops[i]
    if (s && o && (s.haulageDeliveryRevenueGbp ?? null) !== (o.haulageDeliveryRevenueGbp ?? null)) {
      changes.push("Stop revenue")
      break
    }
  }
  // Stop-level price (direct injection), which sets what the whole route costs
  for (let i = 0; i < maxStops; i++) {
    const s = route.stops[i]
    const o = original.stops[i]
    if (s && o && (s.directInjectionCostGbp ?? null) !== (o.directInjectionCostGbp ?? null)) {
      changes.push("Direct injection cost")
      break
    }
  }
  return changes.length ? changes : ["No changes"]
}

/** Is the route unchanged from its original? Unchanged routes are skipped on save. */
export function routeEqualsOriginal(
  route: PlannedRoute,
  original: PlannedRoute | undefined,
): boolean {
  if (!original) return false
  const d = (date: Date) => format(date, "yyyy-MM-dd")
  const t = (date: Date) => format(date, "HH:mm:ss")
  if (route.code !== original.code) return false
  if (route.depot !== original.depot) return false
  if (route.status !== original.status) return false
  if (route.isRecurring !== original.isRecurring) return false
  if (d(route.date) !== d(original.date)) return false
  if (
    route.recurringEndDate && original.recurringEndDate
      ? d(route.recurringEndDate) !== d(original.recurringEndDate)
      : route.recurringEndDate !== original.recurringEndDate
  )
    return false
  const origDays = [...(original.recurringDays || [])].sort()
  const currDays = [...(route.recurringDays || [])].sort()
  if (origDays.length !== currDays.length || origDays.some((day, i) => day !== currDays[i]))
    return false
  if (t(route.plannedStartTime) !== t(original.plannedStartTime)) return false
  if (t(route.plannedEndTime) !== t(original.plannedEndTime)) return false
  if (route.category !== original.category) return false
  if (route.operator !== original.operator) return false
  if (route.vehicleType !== original.vehicleType) return false
  const origExcl = (original.excludedDates ?? [])
    .map((x: Date) => d(x))
    .sort()
    .join(",")
  const currExcl = (route.excludedDates ?? [])
    .map((x: Date) => d(x))
    .sort()
    .join(",")
  if (origExcl !== currExcl) return false
  if (route.stops.length !== original.stops.length) return false
  for (let i = 0; i < route.stops.length; i++) {
    const s = route.stops[i]
    const o = original.stops[i]
    if (s.locationId !== o.locationId || s.sequence !== o.sequence) return false
    if (
      t(s.plannedArrival) !== t(o.plannedArrival) ||
      t(s.plannedDeparture) !== t(o.plannedDeparture)
    )
      return false
    if ((s.serviceTimeMinutes ?? 30) !== (o.serviceTimeMinutes ?? 30)) return false
    if ((s.slotType || "shipper_collection") !== (o.slotType || "shipper_collection"))
      return false
    if ((s.haulageDeliveryRevenueGbp ?? null) !== (o.haulageDeliveryRevenueGbp ?? null))
      return false
    if ((s.directInjectionCostGbp ?? null) !== (o.directInjectionCostGbp ?? null)) return false
  }
  return true
}

/** 1 if `time` falls on a later day than `referenceDate` (an overnight stop), else 0. */
export function calculateDayOffset(time: Date, referenceDate: Date): number {
  return startOfDay(time).getTime() > startOfDay(referenceDate).getTime() ? 1 : 0
}

/** HH:mm of the clock time; a next-day time is written as its clock time plus a day offset. */
export function formatTimeForDb(time: Date): string {
  const hours = time.getHours()
  const minutes = time.getMinutes()
  return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`
}

/** The days array to save: none for a one-off route, the route's own weekday if none chosen. */
export function recurringDaysForSave(route: PlannedRoute): DayOfWeek[] {
  return route.isRecurring
    ? route.recurringDays && route.recurringDays.length > 0
      ? route.recurringDays
      : [dayOfWeekOf(route.date)]
    : []
}

/** Excluded dates still ahead of today, as yyyy-MM-dd. Past ones are not sent. */
export function futureExcludedDates(excludedDates: Date[] | undefined): string[] {
  return (excludedDates ?? [])
    .filter((d: Date) => isAfter(startOfDay(d), startOfDay(new Date())))
    .map((d: Date) => format(d, "yyyy-MM-dd"))
}

/** The stops of a planned route as save_planned_route takes them. */
export function plannedStopsPayload(route: PlannedRoute) {
  return route.stops.map((stop) => ({
    location_id: stop.locationId,
    sequence: stop.sequence,
    planned_arrival: formatTimeForDb(stop.plannedArrival),
    planned_departure: formatTimeForDb(stop.plannedDeparture),
    arrival_day_offset: calculateDayOffset(stop.plannedArrival, route.date),
    departure_day_offset: calculateDayOffset(stop.plannedDeparture, route.date),
    service_time_minutes: stop.serviceTimeMinutes || 30,
    stop_type: stop.slotType || "shipper_collection",
    stop_services: stop.slotServices || [],
    stop_pnl: stop.slotPnl || [],
    availability_slot_id: stop.slotId,
    haulage_delivery_revenue_gbp:
      stop.slotType === "haulage_delivery" ? (stop.haulageDeliveryRevenueGbp ?? null) : null,
    direct_injection_cost_gbp:
      stop.slotType === "direct_injection" ? (stop.directInjectionCostGbp ?? null) : null,
  }))
}
